
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRecentOrders } from '@/hooks/useRecentOrders';
import { Package, ArrowRight, Truck } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'shipped':
      case 'delivered':
        return 'default';
      case 'pending':
      case 'processing':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
};

const RecentOrdersPreview = () => {
  const { recentOrders, isLoading } = useRecentOrders(3);
  const navigate = useNavigate();

  return (
    <Card className="profile-card border-0 overflow-hidden">
      <CardHeader className="bg-gradient-secondary/30 border-b">
        <CardTitle className="flex items-center gap-3 font-playfair text-xl">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Truck className="h-5 w-5 text-primary" />
          </div>
          Recent Orders
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        )}
        {!isLoading && recentOrders.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2" />
            <p>You haven't placed any orders yet.</p>
          </div>
        )}
        {!isLoading && recentOrders.map((order) => (
          <div key={order.id} className="flex items-center justify-between p-4 bg-gradient-muted/50 rounded-xl border border-border/50 hover:shadow-card transition-all duration-200">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-xl shadow-sm">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Order #{order.order_number}</p>
                <p className="text-sm text-muted-foreground font-medium">
                  {format(new Date(order.created_at), 'dd MMMM, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant={getStatusVariant(order.status)} className="capitalize font-medium">{order.status}</Badge>
              <p className="font-bold text-lg text-foreground font-playfair">${Number(order.total_amount).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </CardContent>
      {recentOrders.length > 0 && (
        <CardFooter className="bg-gradient-secondary/20 border-t">
            <Button variant="outline" className="w-full hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-medium" onClick={() => navigate('/settings?tab=orders')}>
                View All Orders <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default RecentOrdersPreview;
